import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const MARGIN = 8;
const FOOTER_H = 7;
const JPEG_Q = 0.92;

// Intervalles (en px CSS, relatifs au haut de l'élément) des blocs à ne jamais couper :
// tout élément visible qui tient dans une page. Les grands conteneurs sont ignorés
// (on coupe alors entre leurs enfants).
function atomicIntervals(el, pageCss) {
  const base = el.getBoundingClientRect().top;
  const out = [];
  el.querySelectorAll("*").forEach((node) => {
    const r = node.getBoundingClientRect();
    if (r.height <= 0 || r.width <= 0 || r.height > pageCss || node.hasAttribute("data-pdf-breakable")) return;
    out.push([r.top - base, r.bottom - base]);
  });
  return out;
}

// Choisit la coupure la plus basse ≤ limit qui ne traverse aucun bloc atomique.
function pickCut(intervals, start, limit, pageCss) {
  let y = limit;
  for (let guard = 0; guard < 200; guard++) {
    let lowest = Infinity;
    for (const [top, bottom] of intervals) {
      if (top < y - 0.5 && bottom > y + 0.5 && top < lowest) lowest = top;
    }
    if (lowest === Infinity) break;
    y = lowest;
  }
  return y <= start + pageCss * 0.3 ? limit : y;
}

function addCanvas(pdf, canvas, el, addPageFirst, opts) {
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const usableW = pageW - MARGIN * 2;
  const usableH = pageH - MARGIN * 2 - FOOTER_H;
  const imgW = usableW;
  const imgH = (canvas.height * imgW) / canvas.width;
  if (addPageFirst) pdf.addPage();

  if (imgH <= usableH) {
    pdf.addImage(canvas.toDataURL("image/jpeg", JPEG_Q), "JPEG", MARGIN, MARGIN, imgW, imgH);
    return;
  }

  const cssW = el.getBoundingClientRect().width;
  const pxPerCss = canvas.width / cssW;
  const mmPerCss = imgW / cssW;
  const pageCss = usableH / mmPerCss;
  const totalCssRaw = canvas.height / pxPerCss;
  const intervals = atomicIntervals(el, pageCss);
  // Ignore le vide de fin (padding) pour ne jamais produire une dernière page blanche.
  const contentEnd = intervals.reduce((m, [, b]) => Math.max(m, b), 0);
  const totalCss = contentEnd > 0 ? Math.min(totalCssRaw, contentEnd + 6) : totalCssRaw;

  let start = 0;
  let first = true;
  while (start < totalCss - 0.5) {
    if (!first) pdf.addPage();
    first = false;
    const limit = Math.min(totalCss, start + pageCss);
    const cut = limit >= totalCss ? totalCss : pickCut(intervals, start, limit, pageCss);
    const slice = document.createElement("canvas");
    slice.width = canvas.width;
    slice.height = Math.max(1, Math.round((cut - start) * pxPerCss));
    const ctx = slice.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(canvas, 0, Math.round(start * pxPerCss), canvas.width, slice.height, 0, 0, canvas.width, slice.height);
    pdf.addImage(slice.toDataURL("image/jpeg", JPEG_Q), "JPEG", MARGIN, MARGIN, imgW, (cut - start) * mmPerCss);
    start = cut;
  }
}

const BRAND = "M2HAT Psychologie";

// Pied de page uniforme : mention à gauche (par défaut la marque), « page x / y » à droite.
function addFooters(pdf, opts) {
  const n = pdf.getNumberOfPages();
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const label = String(opts.footer || BRAND);
  for (let i = 1; i <= n; i++) {
    pdf.setPage(i);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(110, 92, 83);
    pdf.setDrawColor(229, 220, 209);
    pdf.line(MARGIN, pageH - MARGIN - 2.5, pageW - MARGIN, pageH - MARGIN - 2.5);
    pdf.text(label, MARGIN, pageH - MARGIN + 1, { maxWidth: pageW - MARGIN * 2 - 22 });
    pdf.text(`page ${i} / ${n}`, pageW - MARGIN, pageH - MARGIN + 1, { align: "right" });
  }
}

// Propriétés du fichier : aucune référence à un outil tiers, seulement le cabinet.
function setMeta(pdf, filename, opts) {
  const title = opts.title || String(filename).replace(/\.pdf$/i, "").replace(/[-_]+/g, " ");
  pdf.setProperties({ title, subject: opts.subject || title, author: BRAND, creator: BRAND, keywords: BRAND });
}

const render = (el) => html2canvas(el, { scale: 2, backgroundColor: "#ffffff", useCORS: true });

// Génère un PDF A4 à partir d'éléments DOM (un nouveau départ de page par élément),
// sans passer par l'impression navigateur. Les pages longues sont coupées entre les blocs,
// jamais au milieu d'un paragraphe, d'une carte ou d'une ligne. opts.footer : mention en pied de page.
async function buildPdf(els, filename, opts = {}) {
  const list = els.filter(Boolean);
  if (!list.length) return null;
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait", compress: true });
  setMeta(pdf, filename, opts);
  const html = document.documentElement;
  const wasDark = html.classList.contains("dark");
  if (wasDark) html.classList.remove("dark");
  try {
    for (let i = 0; i < list.length; i++) addCanvas(pdf, await render(list[i]), list[i], i > 0, opts);
  } finally {
    if (wasDark) html.classList.add("dark");
  }
  addFooters(pdf, opts);
  return pdf;
}

export async function downloadElementAsPdf(el, filename = "document.pdf", opts) {
  const pdf = await buildPdf([el], filename, opts);
  if (pdf) pdf.save(filename);
}

export async function downloadElementsAsPdf(els, filename = "document.pdf", opts) {
  const pdf = await buildPdf(els, filename, opts);
  if (pdf) pdf.save(filename);
}

export async function elementsToPdfBlob(els, opts = {}) {
  const pdf = await buildPdf(els, opts.filename || "document.pdf", opts);
  return pdf ? pdf.output("blob") : null;
}
